import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Link2, Loader2, Save, Trash2 } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const GOLD_INK = "var(--brand-accent-ink)";
const SOURCE_LABEL = {
  stored: "console keys (encrypted)",
  env: "backend/.env fallback",
  none: "not configured — stub mode",
};
const SOURCE_STYLE = {
  stored: "bg-teal-50 border-teal-200 text-teal-700",
  env: "bg-slate-100 border-slate-300 text-slate-600",
  none: "bg-amber-50 border-amber-200 text-amber-700",
};

export default function PayPalCredentialsCard({ config, onChanged }) {
  const gw = config.gateway;
  const cred = config.credentials;
  const [mode, setMode] = useState(cred.mode || "sandbox");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await platformApi.put("/billing/credentials",
        { mode, client_id: clientId, client_secret: clientSecret });
      toast.success(`PayPal credentials saved (${data.mode}) — client id ${data.client_id_masked}${data.artifacts_cleared ? " · previous artifacts cleared (mode changed)" : ""}`);
      setClientId(""); setClientSecret("");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const clearStored = async () => {
    setClearing(true);
    try {
      const { data } = await platformApi.delete("/billing/credentials");
      toast.success(data.env_fallback
        ? "Stored keys cleared — gateway now uses the backend/.env fallback"
        : "Stored keys cleared — gateway is in stub mode until keys are saved");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setClearing(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await platformApi.post("/billing/connect");
      toast.success(`PayPal connected (${data.mode}) — product, plans & webhook ready`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="paypal-credentials-card">
      <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[13px] font-bold flex items-center gap-2" style={{ color: "var(--brand-primary)" }}>
          <KeyRound className="h-4 w-4" style={{ color: GOLD_INK }} />
          PayPal gateway
          <span data-testid="paypal-cred-status"
            className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border ${SOURCE_STYLE[cred.source] || SOURCE_STYLE.none}`}>
            {SOURCE_LABEL[cred.source] || SOURCE_LABEL.none}
          </span>
          {gw.configured && (
            <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-50 border-slate-200 text-slate-500">
              {gw.mode} mode
            </span>
          )}
        </p>
        {gw.configured && (
          <div className="flex items-center gap-3 flex-wrap">
            {gw.connected
              ? <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-teal-50 border-teal-200 text-teal-700">connected</span>
              : <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-amber-50 border-amber-200 text-amber-700">keys ok — not connected</span>}
            <Button onClick={connect} disabled={connecting} data-testid="paypal-connect-btn"
              className="rounded-none brand-accent-bg border-0 h-8 text-[11px]">
              {connecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
              {gw.connected ? "Re-verify connection" : "Connect PayPal"}
            </Button>
          </div>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">
        {cred.source !== "none" && (
          <p className="text-[11px] font-mono text-slate-400" data-testid="paypal-cred-summary">
            client id {cred.client_id_masked || "••••"} · secret {cred.client_secret_set ? "set (write-only)" : "—"}
            {cred.updated_by ? ` · saved by ${cred.updated_by}` : ""}
            {cred.updated_at ? ` · ${new Date(cred.updated_at).toLocaleString("en-GB")}` : ""}
          </p>
        )}
        {gw.connected && (
          <p className="text-[11px] font-mono text-slate-400" data-testid="paypal-artifacts">
            product {gw.product_id} · webhook {gw.webhook_id} · plans{" "}
            {Object.entries(gw.plan_ids).map(([k, v]) => `${k}:${v}`).join(" ")}
          </p>
        )}

        <div className="grid md:grid-cols-[140px_1fr_1fr_auto] gap-3 items-end">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Mode</p>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="rounded-none h-9 text-[12px]" data-testid="paypal-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="sandbox" className="text-[12px]" data-testid="paypal-mode-sandbox">sandbox</SelectItem>
                <SelectItem value="live" className="text-[12px]" data-testid="paypal-mode-live">live</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Client ID</p>
            <Input data-testid="paypal-client-id-input" value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder={cred.source === "stored" ? `saved ${cred.client_id_masked} — blank keeps it` : "PayPal REST Client ID"}
              className="rounded-none h-9 font-mono text-[12px]" autoComplete="off" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Client Secret</p>
            <PasswordInput data-testid="paypal-client-secret-input" value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder={cred.source === "stored" ? "saved (write-only) — blank keeps it" : "PayPal REST Client Secret"}
              className="rounded-none h-9 font-mono text-[12px]" autoComplete="new-password" />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={save} data-testid="paypal-credentials-save-btn"
              disabled={saving || (cred.source !== "stored" && (!clientId || !clientSecret))}
              className="rounded-none h-9 text-[11px] bg-[#1E293B] hover:bg-slate-700 text-white">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save keys
            </Button>
            {cred.source === "stored" && (
              <Button onClick={clearStored} disabled={clearing} variant="outline"
                data-testid="paypal-credentials-clear-btn"
                className="rounded-none h-9 text-[11px] border-slate-300 text-slate-500 hover:text-red-600">
                {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
        </div>

        <p className="text-[11px] text-slate-400 leading-relaxed" data-testid="paypal-precedence-note">
          Keys are encrypted at rest and never displayed again — only the last 4 characters of the Client ID are shown.
          <span className="text-slate-500 font-medium"> Keys saved here always take precedence; </span>
          PAYPAL_* variables in backend/.env act only as a fallback when nothing is saved in the console.
          Get sandbox credentials at developer.paypal.com → Apps &amp; Credentials. The webhook is created
          automatically on Connect. Every save, clear and connect is written to the platform audit trail.
        </p>
      </div>
    </div>
  );
}
